<template>
  <div
    ref="dropZone"
    :dropzone="canDrop"
    :class="{ 'drag-over': dragOver, 'drop-after': dropAfter, 'drop-before': !dropAfter }"
    class="col-12 col-sm-6 col-md-4 col-lg-3 q-pa-sm"
    @drop="drop"
    @dragover.prevent
    @dragenter="dragEnter"
    @dragleave="dragLeave"
  >
    <q-card
      :draggable="editing"
      class="cursor-pointer"
      horizontal
      flat
      bordered
      :class="{ dragging: dragging, selected: selected }"
      @dragstart.stop="dragStart($event)"
      @dragend="dragEnd"
      @click.shift.stop="select()"
      @click.exact.stop="$emit('select-support-article', supportArticle)"
    >
      <q-img
        class="col"
        :ratio="16/9"
        :src="supportArticle.thumbnailUrl"
      />
      <q-card-section
        horizontal
        class="q-pa-xs"
      >
        <q-item class="q-pr-none">
          <q-item-section class="col">
            <q-item-label
              style="font-size: 18px;"
            >
              {{ supportArticle.title }}
            </q-item-label>
            <q-item-label caption>
              {{ supportArticle.description }}
            </q-item-label>
          </q-item-section>
        </q-item>
      </q-card-section>
      <q-menu
        v-if="currentUser && currentUser.superUser"
        touch-position
        context-menu
      >
        <q-list>
          <q-item
            v-close-popup
            clickable
            @click.stop="$emit('create-support-article', supportArticle)"
          >
            <q-item-section avatar>
              <q-icon name="add" />
            </q-item-section>
            <q-item-section>
              New Support Article
            </q-item-section>
          </q-item>

          <q-separator />

          <q-item
            v-close-popup
            clickable
            :disable="!canEdit"
            @click.stop="$emit('edit', supportArticle)"
          >
            <q-item-section avatar>
              <q-icon name="edit" />
            </q-item-section>
            <q-item-section>
              Edit
            </q-item-section>
          </q-item>
          <q-separator />
          <q-item
            v-close-popup
            clickable
            :disable="!canDelete"
            @click.stop="deleteSupportArticles()"
          >
            <q-item-section avatar>
              <q-icon name="delete" />
            </q-item-section>
            <q-item-section>
              Delete {{ selectedSupportArticleIds.length ? '(' + selectedSupportArticleIds.length + ' SupportArticles)' : supportArticle.title }}
            </q-item-section>
          </q-item>
        </q-list>
      </q-menu>
    </q-card>
  </div>
</template>

<script>

export default {
  name: 'SupportArticleMenuItem',
  props: {
    supportArticle: {
      type: Object,
      required: false,
      default: null
    },
    draggingCategoryId: {
      type: Number,
      required: false,
      default: null
    },
    draggingSupportArticleId: {
      type: Number,
      required: false,
      default: null
    },
    selectedSupportArticleIds: {
      type: Array,
      required: false,
      default () {
        return []
      }
    },
    editing: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['select-support-article', 'create-support-article', 'edit', 'drag-start-support-article', 'drag-end-support-article', 'delete-support-articles', 'unselect-support-articles'],
  data () {
    return {
      dragOver: 0,
      dragging: false,
      dropAfter: false
    }
  },
  computed: {
    canDrop () {
      return this.canEdit && !this.draggingCategoryId && this.draggingSupportArticleId !== this.supportArticle.id
    },
    selected () {
      return _.includes(this.selectedSupportArticleIds, this.supportArticle?.id)
    },
    canEdit () {
      return !!this.currentUser?.superUser
    },
    canDelete () {
      return !!this.currentUser?.superUser
    }
  },
  context: ['currentUser'],
  methods: {
    dragStart (event) {
      if (!this.canEdit) {
        event.preventDefault()
        event.stopPropagation()
        return false
      }

      this.$emit('drag-start-support-article', this.supportArticle.id)

      // Use set timeout because we want the drag bitmap to be the version prior to adding the dragging class
      setTimeout(() => { this.dragging = true }, 0)
    },
    dragEnd () {
      this.dragging = false
    },
    drop (e) {
      if (!this.canDrop) return

      this.$emit('drag-end-support-article', this.supportArticle.id, this.dropAfter)
      this.dragOver = 0

      e.stopPropagation()
    },
    dragEnter (e) {
      if (!this.canDrop) return

      // Determine if we are left or right of center, drop before or after supportArticle
      const rect = this.$refs.dropZone.getBoundingClientRect()
      if ((e.clientX - rect.left) < (rect.right - rect.left) / 2) {
        this.dropAfter = false
      } else {
        this.dropAfter = true
      }

      this.dragOver += 1

      e.stopPropagation()
    },
    dragLeave (e) {
      if (!this.canDrop) return

      this.dragOver -= 1

      e.stopPropagation()
    },
    deleteSupportArticles () {
      let label
      if (this.selectedSupportArticleIds.length) {
        label = `${this.selectedSupportArticleIds.length} supportArticles`
      } else {
        label = this.supportArticle.title
      }

      this.$q.dialog({
        title: 'Confirm Delete',
        message: `Are you sure you want to delete ${label}?`,
        ok: {
          label: 'Delete',
          color: 'primary',
          autofocus: true
        },
        cancel: {
          label: 'Cancel',
          color: 'secondary'
        },
        focus: 'cancel'
      }).onOk(() => {
        if (this.selectedSupportArticleIds.length) {
          this.$emit('delete-support-articles', this.selectedSupportArticleIds)
        } else {
          this.$emit('delete-support-articles', [this.supportArticle.id])
        }
      })
    },
    select () {
      this.$emit('select-support-article', this.supportArticle)
    },
    unselect () {
      this.$emit('unselect-support-articles')
    }
  }
}
</script>

<style>
</style>

<style lang="scss" scoped>
.selected {
  border: solid 2px blue;
}

.drag-over.drop-before {
  border-left: solid 3px #000;
}
.drag-over.drop-after {
  border-right: solid 3px #000;
}

.dragging {
  border: solid 2px $grey-5;
  opacity: 0.5;
}
</style>
